import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Sparkles, RefreshCw, EyeOff, Languages } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';

import {
  SMS_TEXT_MAX_LENGTH,
  isSmsTextOverLimit,
} from '@/features/campaign-texts/smsTextLimit';

import { PreviewDevExactPromptButton } from './PreviewDevExactPromptButton';
import { PreviewTextItem } from './PreviewTextItem';

interface GeneratedTextsPreviewProps {
  texts: string[];
  /** Optional English translations (null = missing). */
  translations?: (string | null)[] | null;
  onSave: (selected: string[]) => void;
  onSaveAndHide?: (selected: string[]) => void;
  onDismiss: () => void;
  onRegenerate?: (unselectedIndices: number[]) => void;
  /** Regenerate indices whose original SMS body exceeds the segment limit (uses `texts`, not translations). */
  onRegenerateOverLength?: () => void;
  onTranslateAll?: () => void;
  onTranslateMissing?: () => void;
  isRegenerating?: boolean;
  isTranslating?: boolean;
  /** Dev: copy exact system + user prompt for this generation (clipboard). */
  onDevCopyExactPrompt?: () => void | Promise<void>;
  isDevCopyExactPromptPending?: boolean;
  /** When true, fills parent height so footer stays at bottom of card. */
  fillHeight?: boolean;
}

export function GeneratedTextsPreview({
  texts,
  translations,
  onSave,
  onSaveAndHide,
  onDismiss,
  onRegenerate,
  onRegenerateOverLength,
  onTranslateAll,
  onTranslateMissing,
  isRegenerating,
  isTranslating,
  onDevCopyExactPrompt,
  isDevCopyExactPromptPending,
  fillHeight = false,
}: GeneratedTextsPreviewProps) {
  const [selected, setSelected] = useState<Set<number>>(() => new Set(texts.map((_, i) => i)));
  const [showEnglish, setShowEnglish] = useState(false);
  const prevTextsRef = useRef<string[]>(texts);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasAnyTranslation = Boolean(translations?.some((t) => t != null));
  const missingCount = translations?.filter((t) => t == null).length ?? texts.length;
  const showTranslateAll = (onTranslateAll || onTranslateMissing) && (!translations || missingCount === texts.length);
  const showTranslateMissing = onTranslateMissing && translations && missingCount > 0 && missingCount < texts.length;

  const displayTexts = useMemo(
    () =>
      hasAnyTranslation && showEnglish
        ? texts.map((t, i) => (translations?.[i] ?? t) as string)
        : texts,
    [texts, translations, hasAnyTranslation, showEnglish],
  );

  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (texts.length > 0 && el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [texts.length]);

  useEffect(() => {
    const prev = prevTextsRef.current;
    if (prev.length === texts.length) {
      const changedIndices: number[] = [];
      for (let i = 0; i < texts.length; i++) {
        if (prev[i] !== texts[i]) changedIndices.push(i);
      }
      if (changedIndices.length > 0) {
        setSelected((s) => {
          const next = new Set(s);
          for (const idx of changedIndices) next.add(idx);
          return next;
        });
      }
    }
    prevTextsRef.current = texts;
  }, [texts]);

  const toggleItem = useCallback((idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const allSelected = useMemo(() => selected.size === texts.length, [selected.size, texts.length]);
  const unselectedCount = texts.length - selected.size;

  const toggleAll = useCallback(() => {
    setSelected(allSelected ? new Set() : new Set(texts.map((_, i) => i)));
  }, [allSelected, texts]);

  const handleSave = useCallback(() => {
    const chosen = texts.filter((_, i) => selected.has(i));
    if (chosen.length > 0) onSave(chosen);
  }, [texts, selected, onSave]);

  const handleRegenerate = useCallback(() => {
    if (unselectedCount === 0 || !onRegenerate) return;
    const indices = texts.map((_, i) => i).filter((i) => !selected.has(i));
    onRegenerate(indices);
  }, [unselectedCount, onRegenerate, texts, selected]);

  const handleSaveAndHide = useCallback(() => {
    const chosen = texts.filter((_, i) => selected.has(i));
    if (chosen.length > 0 && onSaveAndHide) onSaveAndHide(chosen);
  }, [texts, selected, onSaveAndHide]);

  const showRegen = onRegenerate && unselectedCount > 0;
  const hasOverLengthOriginal = useMemo(
    () => texts.some((t) => isSmsTextOverLimit(t)),
    [texts],
  );
  const showRegenOver160 = Boolean(onRegenerateOverLength) && hasOverLengthOriginal;
  const containerClass = fillHeight
    ? 'flex min-h-0 flex-1 flex-col overflow-hidden border-b border-border'
    : 'flex max-h-[min(480px,60vh)] min-h-[140px] flex-col overflow-hidden border-b border-border';

  const content = (
    <div ref={containerRef} className={containerClass}>
      <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between rounded-t-lg border-b border-border bg-background px-3 py-2 shadow-sm backdrop-blur-sm">
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase text-muted-foreground">
            <Sparkles className="h-3 w-3 text-primary" />
            Generated ({selected.size}/{texts.length})
          </span>
          <div className="flex items-center gap-1">
            {hasAnyTranslation && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 text-[10px]"
                onClick={() => setShowEnglish((v) => !v)}
              >
                <Languages className="h-3 w-3" />
                {showEnglish ? 'Show original' : 'Show English'}
              </Button>
            )}
            {showTranslateAll && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 text-[10px]"
                onClick={onTranslateAll}
                disabled={isTranslating}
              >
                {isTranslating ? <LoadingSpinner className="h-3 w-3" /> : <Languages className="h-3 w-3" />}
                Translate
              </Button>
            )}
            {showTranslateMissing && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 text-[10px]"
                onClick={onTranslateMissing}
                disabled={isTranslating}
              >
                {isTranslating ? <LoadingSpinner className="h-3 w-3" /> : <Languages className="h-3 w-3" />}
                Translate {missingCount} missing
              </Button>
            )}
            {onDevCopyExactPrompt && (
              <PreviewDevExactPromptButton
                onCopy={onDevCopyExactPrompt}
                pending={isDevCopyExactPromptPending}
              />
            )}
            <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={toggleAll}>
              {allSelected ? 'Deselect all' : 'Select all'}
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDismiss}>
              <X className="h-3 w-3" />
            </Button>
          </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto space-y-1 px-2 py-1.5 scrollbar-thin">
        <AnimatePresence>
          {displayTexts.map((text, i) => (
            <PreviewTextItem
              key={`${i}-${text.slice(0, 20)}`}
              text={text}
              lengthSourceText={texts[i]}
              index={i}
              isSelected={selected.has(i)}
              onToggle={toggleItem}
              lengthLimit={SMS_TEXT_MAX_LENGTH}
            />
          ))}
        </AnimatePresence>
      </div>
      <div className="z-20 shrink-0 space-y-1.5 rounded-b-lg border-t border-border bg-background px-3 pt-2 pb-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs flex-1"
            onClick={handleSave} disabled={selected.size === 0}>
            <Check className="h-3 w-3" />
            Save {selected.size}
          </Button>
          {showRegenOver160 && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={() => onRegenerateOverLength?.()}
              disabled={isRegenerating}
            >
              {isRegenerating ? <LoadingSpinner className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />}
              Regen &gt;160
            </Button>
          )}
          {showRegen && (
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs flex-1"
              onClick={handleRegenerate} disabled={isRegenerating}>
              {isRegenerating ? <LoadingSpinner className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />}
              Regen {unselectedCount}
            </Button>
          )}
          {onSaveAndHide && (
            <Button size="sm" className="h-7 gap-1 text-xs flex-1"
              onClick={handleSaveAndHide} disabled={selected.size === 0}>
              <EyeOff className="h-3 w-3" />
              Use & Hide
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={fillHeight ? 'flex min-h-0 flex-1 flex-col overflow-hidden w-full' : 'w-full'}
    >
      {content}
    </motion.div>
  );
}
